import {
  type Auth,
  GoogleAuthProvider,
  type User,
  onAuthStateChanged,
  signInWithPopup,
  signOut as firebaseSignOut,
} from 'firebase/auth';

import { roleSchema, type Role } from '../../core/policy/roles';
import { WORKSPACE_DOMAIN } from '../../core/policy/identity';
import {
  type AuthPort,
  type AuthenticatedUser,
  SignInError,
  type SignInFailureCode,
} from '../../features/auth/application/ports/authPort';

/**
 * Firebase implementation of `AuthPort` (blueprint §11.1).
 *
 * Raw platform errors are translated here and never escape (§4.5). UI code
 * consumes `SignInError` and has no idea Firebase exists.
 */

/**
 * Reads roles from the token's custom claims.
 *
 * Unknown values are dropped rather than trusted: the claim is signed by the
 * platform, but a role this build does not recognise is more likely a stale
 * deployment than a new permission, and silently ignoring it fails closed.
 */
function readRoles(claims: Record<string, unknown>): readonly Role[] {
  const raw = claims['roles'];
  if (!Array.isArray(raw)) return [];

  return raw.flatMap((value) => {
    const parsed = roleSchema.safeParse(value);
    return parsed.success ? [parsed.data] : [];
  });
}

async function toAuthenticatedUser(user: User): Promise<AuthenticatedUser> {
  const token = await user.getIdTokenResult();
  return {
    uid: user.uid,
    email: user.email,
    emailVerified: user.emailVerified,
    displayName: user.displayName,
    roles: readRoles(token.claims),
  };
}

function translateSignInError(error: unknown): SignInError {
  // `'code' in error` already narrows to a type carrying `code`, so an
  // assertion here would be redundant.
  const code = typeof error === 'object' && error !== null && 'code' in error
    ? String(error.code)
    : '';

  const mapping: Record<string, { code: SignInFailureCode; message: string }> = {
    'auth/popup-blocked': {
      code: 'popup_blocked',
      message: 'المتصفح منع نافذة تسجيل الدخول. اسمح بالنوافذ المنبثقة وحاول مجددًا.',
    },
    'auth/popup-closed-by-user': {
      code: 'popup_closed',
      message: 'أُغلقت نافذة تسجيل الدخول قبل إتمامها.',
    },
    'auth/cancelled-popup-request': {
      code: 'popup_closed',
      message: 'أُغلقت نافذة تسجيل الدخول قبل إتمامها.',
    },
    'auth/network-request-failed': {
      code: 'network',
      message: 'تعذّر الاتصال. تحقق من الإنترنت وحاول مجددًا.',
    },
  };

  const known = mapping[code];
  if (known) return new SignInError(known.code, known.message);

  // §14.1 — the user gets a safe message; the platform code is not shown, since
  // it says more about our stack than it does about their problem.
  return new SignInError('unknown', 'تعذّر تسجيل الدخول. حاول مرة أخرى.');
}

export function createFirebaseAuthAdapter(auth: Auth): AuthPort {
  const provider = new GoogleAuthProvider();

  /*
   * `hd` pre-selects the school account in Google's chooser. It is a
   * convenience only — it travels from the browser and anyone can remove it.
   * Every real domain check happens against the verified token, in the security
   * rules and in `refuseSignInReason`. Treating this as a restriction is the
   * mistake it invites.
   */
  provider.setCustomParameters({ hd: WORKSPACE_DOMAIN, prompt: 'select_account' });

  return {
    async signInWithGoogle() {
      try {
        const credential = await signInWithPopup(auth, provider);
        return await toAuthenticatedUser(credential.user);
      } catch (error) {
        throw translateSignInError(error);
      }
    },

    async signOut() {
      await firebaseSignOut(auth);
    },

    observeUser(onChange) {
      return onAuthStateChanged(auth, (user) => {
        if (!user) {
          onChange(null);
          return;
        }
        void toAuthenticatedUser(user).then(onChange);
      });
    },

    async refreshRoles() {
      const user = auth.currentUser;
      if (!user) return [];
      // `true` forces a round trip; without it the cached token is returned and
      // a freshly-granted role stays invisible until it expires on its own.
      const token = await user.getIdTokenResult(true);
      return readRoles(token.claims);
    },
  };
}
